import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { GripVertical, Pencil, Plus, Star, Trash2, Upload } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import type { Testimonial } from '@/types'
import Button from '@/components/ui/Button'
import { Field, Input, Textarea } from '@/components/ui/Field'
import Modal from '@/components/ui/Modal'
import PageHeader from '@/components/ui/PageHeader'
import LogoLoader from '@/components/ui/LogoLoader'
import { Table, Row, Cell, EmptyRow, actionsColumn, actionsCellClass } from '@/components/ui/Table'
import Badge from '@/components/ui/Badge'
import { useToast } from '@/components/ui/toast'
import { Stars, TestimonialAvatar, TestimonialCard } from '@/components/storefront/Testimonials'

/** Matches the backend's max:300 and the column width. */
const QUOTE_MAX = 300

interface Draft {
  id?: number
  name: string
  role: string
  quote: string
  rating: number
  image_path: string
  image_key: string | null
  active: boolean
  sort: number
}

const emptyDraft: Draft = {
  name: '',
  role: '',
  quote: '',
  rating: 5,
  image_path: '',
  image_key: null,
  active: true,
  sort: 0,
}

export default function AdminTestimonials() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<Draft | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<Testimonial | null>(null)
  // Chosen file is held locally and previewed with an object URL, so nothing
  // is uploaded until Save. A cancelled edit therefore leaves no orphan file.
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)

  const testimonials = useQuery({
    queryKey: ['admin-testimonials'],
    queryFn: async () => (await api.get<{ data: Testimonial[] }>('/admin/testimonials')).data.data,
  })

  useEffect(() => {
    if (!file) {
      setPreview(null)
      return
    }
    const url = URL.createObjectURL(file)
    setPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  const invalidate = () => {
    for (const key of ['admin-testimonials', 'storefront-testimonials']) {
      queryClient.invalidateQueries({ queryKey: [key] })
    }
  }

  const close = () => {
    setDraft(null)
    setFile(null)
  }

  const save = useMutation({
    mutationFn: async (d: Draft) => {
      let imagePath = d.image_path
      let imageKey = d.image_key

      // Upload first so the testimonial is only written once we have a real path.
      if (file) {
        const form = new FormData()
        form.append('image', file)
        const uploaded = (
          await api.post<{ image_path: string; image_key: string }>('/admin/testimonials/upload', form)
        ).data
        imagePath = uploaded.image_path
        // Carries the ownership of the new file. Without it the backend cannot
        // tell this upload from a bundled asset and would never clean it up.
        imageKey = uploaded.image_key
      }

      const payload = {
        name: d.name.trim(),
        role: d.role.trim() || null,
        quote: d.quote.trim(),
        rating: d.rating,
        image_path: imagePath || null,
        image_key: imagePath ? imageKey : null,
        active: d.active,
        sort: d.sort,
      }

      return d.id
        ? (await api.patch(`/admin/testimonials/${d.id}`, payload)).data
        : (await api.post('/admin/testimonials', payload)).data
    },
    onSuccess: (res: { message?: string }) => {
      invalidate()
      toast.success(res.message ?? 'Saved')
      close()
    },
    onError: (err) => toast.error('Save failed', errorMessage(err)),
  })

  const remove = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/admin/testimonials/${id}`)).data,
    onSuccess: (res: { message?: string }) => {
      invalidate()
      setConfirmDelete(null)
      toast.success(res.message ?? 'Deleted')
    },
    onError: (err) => {
      setConfirmDelete(null)
      toast.error('Delete failed', errorMessage(err))
    },
  })

  const rows = testimonials.data ?? []

  const openNew = () => {
    setFile(null)
    setDraft({ ...emptyDraft, sort: rows.length })
  }

  const openEdit = (t: Testimonial) => {
    setFile(null)
    setDraft({
      id: t.id,
      name: t.name,
      role: t.role ?? '',
      quote: t.quote,
      rating: t.rating,
      image_path: t.image_path ?? '',
      image_key: t.image_key ?? null,
      active: t.active ?? true,
      sort: t.sort ?? 0,
    })
  }

  if (testimonials.isLoading) return <LogoLoader />

  const shownImage = preview ?? (draft?.image_path || null)

  return (
    <div>
      <PageHeader
        title="Testimonials"
        subtitle="Customer quotes in the home page's What our customers say strip. Lowest sort shows first; only active testimonials appear."
        actions={
          <Button onClick={openNew}>
            <Plus className="size-4" /> New testimonial
          </Button>
        }
      />

      <Table columns={['', 'Customer', 'Rating', 'State', actionsColumn]}>
        {rows.length === 0 ? (
          <EmptyRow colSpan={5}>No testimonials yet. The home page hides the section until you add one.</EmptyRow>
        ) : (
          rows.map((t) => (
            <Row key={t.id} onClick={() => openEdit(t)}>
              <Cell className="w-8 text-foreground/40">
                <GripVertical className="size-4" />
              </Cell>
              <Cell>
                <div className="flex items-center gap-3">
                  <TestimonialAvatar key={t.image_path ?? ''} name={t.name} src={t.image_path} />
                  <div className="min-w-0">
                    <p className="font-medium">{t.name}</p>
                    {t.role && <p className="text-xs text-foreground/60">{t.role}</p>}
                    <p className="max-w-[16rem] truncate text-xs text-foreground/50 sm:max-w-md">"{t.quote}"</p>
                  </div>
                </div>
              </Cell>
              <Cell className="whitespace-nowrap">
                <Stars rating={t.rating} />
              </Cell>
              <Cell>
                <Badge tone={t.active ? 'success' : 'neutral'}>{t.active ? 'Active' : 'Hidden'}</Badge>
              </Cell>
              <Cell className={actionsCellClass}>
                <Button
                  variant="ghost"
                  className="h-8 px-2"
                  onClick={(e) => {
                    e.stopPropagation()
                    openEdit(t)
                  }}
                >
                  <Pencil className="size-4" />
                </Button>
                <button
                  type="button"
                  title={`Delete testimonial from ${t.name}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    setConfirmDelete(t)
                  }}
                  className="-m-2 p-2 text-foreground/50 transition hover:text-danger sm:m-0 sm:ml-2 sm:p-0"
                >
                  <Trash2 className="size-4" />
                </button>
              </Cell>
            </Row>
          ))
        )}
      </Table>

      <Modal
        open={Boolean(draft)}
        onClose={close}
        title={draft?.id ? 'Edit testimonial' : 'New testimonial'}
        width="max-w-2xl"
        footer={
          <>
            <Button variant="outline" onClick={close}>
              Cancel
            </Button>
            <Button
              loading={save.isPending}
              disabled={!draft?.name.trim() || !draft?.quote.trim()}
              onClick={() => draft && save.mutate(draft)}
            >
              Save testimonial
            </Button>
          </>
        }
      >
        {draft && (
          <div className="max-h-[65vh] space-y-4 overflow-y-auto pr-1 scrollbar-thin">
            {/* The real storefront card, so what the admin sees is what visitors get. */}
            <div className="grid place-items-center rounded-xl border border-dashed border-foreground/15 p-4 sm:p-6">
              <TestimonialCard
                fluid
                t={{
                  name: draft.name.trim() || 'Customer name',
                  role: draft.role.trim() || null,
                  quote: draft.quote.trim() || 'Their quote shows here.',
                  rating: draft.rating,
                  image_path: shownImage,
                }}
              />
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <label className="inline-flex h-10 cursor-pointer items-center gap-2 rounded-lg border border-primary/60 bg-primary/20 px-4 text-sm font-semibold text-primary hover:bg-primary/30">
                <Upload className="size-4" />
                {shownImage ? 'Replace photo' : 'Choose photo'}
                <input
                  type="file"
                  className="hidden"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => {
                    setFile(e.target.files?.[0] ?? null)
                    // Cleared so choosing the same file again after Remove still fires.
                    e.target.value = ''
                  }}
                />
              </label>
              {shownImage && (
                <Button
                  variant="outline"
                  onClick={() => {
                    // Saving without a key tells the backend to delete the
                    // uploaded file this testimonial owned, if it had one.
                    setFile(null)
                    setDraft({ ...draft, image_path: '', image_key: null })
                  }}
                >
                  Remove photo
                </Button>
              )}
              <p className="text-xs text-foreground/50">
                Optional. A square photo works best, max 4 MB. Without one, their initials show.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Customer name" htmlFor="name">
                <Input
                  id="name"
                  maxLength={120}
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="Siti Nuraini"
                />
              </Field>
              <Field label="Role or place (optional)" htmlFor="role">
                <Input
                  id="role"
                  maxLength={120}
                  value={draft.role}
                  onChange={(e) => setDraft({ ...draft, role: e.target.value })}
                  placeholder="School sports coordinator, Kerian"
                />
              </Field>
            </div>

            <Field label="Quote" htmlFor="quote">
              <Textarea
                id="quote"
                rows={4}
                maxLength={QUOTE_MAX}
                value={draft.quote}
                onChange={(e) => setDraft({ ...draft, quote: e.target.value })}
                placeholder="What they said about their order"
              />
              <p className="text-right text-xs text-foreground/50">
                {draft.quote.length}/{QUOTE_MAX}
              </p>
            </Field>

            <Field label="Rating">
              <div className="flex items-center" role="radiogroup" aria-label="Rating">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    role="radio"
                    aria-checked={draft.rating === n}
                    aria-label={`${n} out of 5 stars`}
                    onClick={() => setDraft({ ...draft, rating: n })}
                    className="p-2 first:-ml-2"
                  >
                    <Star
                      className={
                        n <= draft.rating ? 'size-6 fill-amber-400 text-amber-400' : 'size-6 text-foreground/25'
                      }
                    />
                  </button>
                ))}
                <span className="ml-1 text-sm text-foreground/60">{draft.rating} of 5</span>
              </div>
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Sort (lowest shows first)" htmlFor="sort">
                <Input
                  id="sort"
                  type="number"
                  min={0}
                  value={draft.sort}
                  onChange={(e) => setDraft({ ...draft, sort: Number(e.target.value) })}
                />
              </Field>
              <Field label="Visibility">
                <label className="inline-flex h-10 cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={draft.active}
                    onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
                    className="size-4 rounded border-foreground/30"
                  />
                  Show this testimonial on the home page
                </label>
              </Field>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        title="Delete testimonial?"
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => confirmDelete && remove.mutate(confirmDelete.id)}
            >
              Delete testimonial
            </Button>
          </>
        }
      >
        <p className="text-sm text-foreground/70">
          The quote from "{confirmDelete?.name}" is removed from the home page, and its uploaded photo is deleted. This
          cannot be undone. To take it down for now, untick Visibility instead.
        </p>
      </Modal>
    </div>
  )
}
